import {useContext, useState, MutableRefObject, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {PlaylistEntity} from '../../../../../../../backend/responses/playlist-entity';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {AppLanguageTag} from '../../../../../../../i18n/types';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import i18n from 'i18next';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Type = 'type',
  State = 'state',
}

export default function PlaylistEntitiesSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [updatePlaylistEntityDialogOpened, setUpdatePlaylistEntityDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {entityId: string; entityName: string; playlistActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // Fetch entities
  const handleGetEntities = async (limit: number, skip: number, search?: string) => {
    // Check id
    if (!id) {
      throw new Error('Empty id.');
    }

    // Load entities
    const result: {totalCount: number; data: Array<PlaylistEntity>} =
      await PlaylistsRepository.instance.getPlaylistEntitiesAsSystemAdministrator(
        id,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistEntity> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.entity.id || '',
        item: obj,
        avatarUrl: obj.entity.picture,
        enabled: obj.isActive === true ? true : false,
        toLink: `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Entities}/${obj.entity.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.entity.name || '',
          },
          {
            columnId: ColumnIdentifier.Type,
            value: t(translations.entity_types[obj.entity.type]) || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value:
              obj.isActive === true
                ? t(translations.playlists.system_administrator.edit.entities.state_value_true)
                : t(translations.playlists.system_administrator.edit.entities.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<PlaylistEntity>({
    fetchData: handleGetEntities,
  });

  const handleAskUpdatePlaylistEntityState = (entityId: string, value: boolean) => {
    // Get entity name
    const entityName: string | undefined = data
      .find((obj) => obj.id === entityId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set entity info for later
    dataToUpdateRef.current = {
      entityId: entityId,
      entityName: entityName || '',
      playlistActiveFlag: value,
    };

    // Open modal
    setUpdatePlaylistEntityDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistEntityState = () => {
    // Close modal
    setUpdatePlaylistEntityDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistEntityState = () => {
    // Close modal
    setUpdatePlaylistEntityDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistEntityState();
  };

  const handleUpdatePlaylistEntityState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const entityId = dataToUpdateRef.current.entityId;
      const value = dataToUpdateRef.current.playlistActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === entityId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        entityId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.playlists.system_administrator.edit.entities.state_value_true)
              : t(translations.playlists.system_administrator.edit.entities.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdatePlaylistEntityState(id || '', entityId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.playlists.system_administrator.edit.entities.errors
            .update_state_playlist_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistEntityState = async (
    playlistId: string,
    entityId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistActiveStateForEntityAsSystemAdministrator(
        entityId,
        playlistId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === entityId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        entityId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.playlists.system_administrator.edit.entities.state_value_true)
              : t(translations.playlists.system_administrator.edit.entities.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.playlists.system_administrator.edit.entities.errors
                .enable_playlist_error_message
            )
          : t(
              translations.playlists.system_administrator.edit.entities.errors
                .disable_playlist_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist with id ${playlistId} to ${value} for entity with id ${entityId}.`,
        error
      );
    }
  };

  // Get playlist name
  let playlistNameLocalized: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.ptPT ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.esES:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.esES ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.glES:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.glES ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.enUS:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.enUS ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.ptBR:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.ptBR ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    default:
      playlistNameLocalized = officeContext.state.playlistForm?.name || '';
      break;
  }

  return (
    <ContentPage>
      <PageHeader
        showSearch={true}
        searchLabel={t(
          translations.playlists.system_administrator.edit.entities.search_input_placeholder
        )}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
        disableSearch={isLoading}
      />
      <PaginatedTable
        avatar={{
          type: 'entity',
        }}
        emptyTableMessage={t(translations.playlists.system_administrator.edit.entities.no_entities)}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(translations.playlists.system_administrator.edit.entities.name_header),
            flexGrow: 2,
          },
          {
            columnId: ColumnIdentifier.Type,
            title: t(translations.playlists.system_administrator.edit.entities.type_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.playlists.system_administrator.edit.entities.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(
                  translations.playlists.system_administrator.edit.entities.state_value_true
                ),
                handleClick: handleAskUpdatePlaylistEntityState,
              },
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <AlertModal
        id="confirm-disable-playlist-entity-state"
        opened={updatePlaylistEntityDialogOpened.disableDialogOpened}
        title={t(
          translations.playlists.system_administrator.edit.entities.disable_playlist_dialog_title,
          {
            name: playlistNameLocalized,
          }
        )}
        content={t(
          translations.playlists.system_administrator.edit.entities.disable_playlist_dialog_message,
          {
            name: dataToUpdateRef.current?.entityName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistEntityState}
        actions={[
          {
            label: t(
              translations.playlists.system_administrator.edit.entities
                .disable_playlist_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdatePlaylistEntityState,
          },
          {
            label: t(
              translations.playlists.system_administrator.edit.entities
                .disable_playlist_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistEntityState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-playlist-entity-state"
        opened={updatePlaylistEntityDialogOpened.enableDialogOpened}
        title={t(
          translations.playlists.system_administrator.edit.entities.enable_playlist_dialog_title,
          {
            name: playlistNameLocalized,
          }
        )}
        content={t(
          translations.playlists.system_administrator.edit.entities.enable_playlist_dialog_message,
          {
            name: dataToUpdateRef.current?.entityName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistEntityState}
        actions={[
          {
            label: t(
              translations.playlists.system_administrator.edit.entities
                .enable_playlist_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistEntityState,
          },
          {
            label: t(
              translations.playlists.system_administrator.edit.entities
                .enable_playlist_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdatePlaylistEntityState,
          },
        ]}
      />
    </ContentPage>
  );
}
